import { useCallback, useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Tooltip,
  Tabs,
  Tab,
  Box,
} from '@mui/material';
import {
  Payment,
  Description,
  AdminPanelSettings,
  GroupAdd,
  CardGiftcard,
  Casino,
  CardMembership,
  LocalParking,
  PersonRemove,
  SportsEsports,
  PlayArrow,
  MoreHoriz,
  CreditCard,
  History,
} from '@mui/icons-material';
import { Pagination } from '@mui/material';
import {
  getUserHistoryAsync,
  selectBalance,
  clearUserHistory
} from '../../redux/slices/balanceSlice';
import Title from '../../components/Title';
import { formatDate } from '../../resources/functions';
import PaymentSearch from './PaymentSearch';
import StripeHistoryTab from './StripeHistoryTab';
import { useTheme } from '@mui/material/styles';

const PREFIX = 'HistoryPage';

const classes = {
  tableContainer: `${PREFIX}-tableContainer`
};

const Root = styled('div')({
  [`& .${classes.tableContainer}`]: {
    margin: '20px 0'
  }
});

const limit = 20;

const getTransactionTypeConfig = (type: string) => {
  switch (type) {
    case 'pay':
      return {
        backgroundColor: 'rgba(76, 175, 80, 0.12)',
        iconColor: '#4CAF50',
        icon: Payment,
        tooltip: 'Оплата'
      };
    case 'contract':
      return {
        backgroundColor: 'rgba(33, 150, 243, 0.12)',
        iconColor: '#2196F3',
        icon: Description,
        tooltip: 'Контракт'
      };
    case 'admin':
      return {
        backgroundColor: 'rgba(103, 58, 183, 0.12)',
        iconColor: '#673AB7',
        icon: AdminPanelSettings,
        tooltip: 'Адміністратор'
      };
    case 'referal':
      return {
        backgroundColor: 'rgba(255, 152, 0, 0.12)',
        iconColor: '#FF9800',
        icon: GroupAdd,
        tooltip: 'Реферал'
      };
    case 'bonus':
      return {
        backgroundColor: 'rgba(78, 205, 196, 0.12)',
        iconColor: '#4ECDC4',
        icon: CardGiftcard,
        tooltip: 'Бонус'
      };
    case 'fortuneWheel':
      return {
        backgroundColor: 'rgba(233, 30, 99, 0.12)',
        iconColor: '#E91E63',
        icon: Casino,
        tooltip: 'Колесо фортуни'
      };
    case 'subscription':
      return {
        backgroundColor: 'rgba(103, 58, 183, 0.12)',
        iconColor: '#673AB7',
        icon: CardMembership,
        tooltip: 'Абонемент'
      };
    case 'stopInZone':
      return {
        backgroundColor: 'rgba(76, 175, 80, 0.12)',
        iconColor: '#4CAF50',
        icon: LocalParking,
        tooltip: 'Зупинка в зоні'
      };
    case 'userDeleted':
      return {
        backgroundColor: 'rgba(244, 67, 54, 0.12)',
        iconColor: '#F44336',
        icon: PersonRemove,
        tooltip: 'Користувач видалений'
      };
    case 'game':
      return {
        backgroundColor: 'rgba(33, 150, 243, 0.12)',
        iconColor: '#2196F3',
        icon: SportsEsports,
        tooltip: 'Гра'
      };
    case 'startGame':
      return {
        backgroundColor: 'rgba(33, 150, 243, 0.12)',
        iconColor: '#2196F3',
        icon: PlayArrow,
        tooltip: 'Старт гри'
      };
    default:
      return {
        backgroundColor: 'rgba(158, 158, 158, 0.12)',
        iconColor: '#9E9E9E',
        icon: MoreHoriz,
        tooltip: 'Інше'
      };
  }
};

const HistoryPage = () => {
  const theme = useTheme();
  const balance = useSelector(selectBalance);
  const dispatch = useDispatch();
  const isDark = theme.palette.mode === 'dark';

  const [activeTab, setActiveTab] = useState(0);
  const [page, setPage] = useState(1);

  const handleChangePage = useCallback(
    (e: any, nextPage: number) => setPage(nextPage),
    []
  );

  useEffect(() => {
    if (activeTab === 1) {
      dispatch(getUserHistoryAsync({ type: 'admin', limit, page }));
    }
  }, [dispatch, page, activeTab]);

  useEffect(() => {
    return () => {
      dispatch(clearUserHistory());
    };
  }, [dispatch]);

  return (
    <Root>
      <Title>Транзакції</Title>

      <Paper
        sx={{
          mb: 3,
          border: isDark ? 'none' : '1px solid #E0E0E0',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{
            borderBottom: `1px solid ${theme.palette.divider}`,
            '& .MuiTabs-indicator': { backgroundColor: '#4ECDC4', height: 3 },
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: 14, minHeight: 52 },
            '& .MuiTab-root.Mui-selected': { color: '#4ECDC4' },
          }}
        >
          <Tab
            icon={<CreditCard sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label="Stripe"
          />
          <Tab
            icon={<History sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label="Транзакції балансу"
          />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {activeTab === 0 && <StripeHistoryTab />}

          {activeTab === 1 && (
            <>
              <PaymentSearch />

              <TableContainer className={classes.tableContainer} component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Тип</TableCell>
                      <TableCell>Адміністратор</TableCell>
                      <TableCell>Телефон клієнта</TableCell>
                      <TableCell>Сума</TableCell>
                      <TableCell>Причина</TableCell>
                      <TableCell>Дата</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {balance.history.items.map(item => {
                      const typeConfig = getTransactionTypeConfig(item.type);
                      const TypeIcon = typeConfig.icon;

                      return (
                        <TableRow key={item.createdAt}>
                          <TableCell>
                            <Tooltip title={typeConfig.tooltip} arrow>
                              <Avatar
                                sx={{
                                  width: 40,
                                  height: 40,
                                  backgroundColor: typeConfig.backgroundColor,
                                  borderRadius: '8px'
                                }}
                              >
                                <TypeIcon sx={{ fontSize: 24, color: typeConfig.iconColor }} />
                              </Avatar>
                            </Tooltip>
                          </TableCell>
                          <TableCell>{item.admin?.login}</TableCell>
                          <TableCell>{item.user?.phone}</TableCell>
                          <TableCell>{item.amount}</TableCell>
                          <TableCell>{item.description || '-'}</TableCell>
                          <TableCell>
                            {formatDate(item.createdAt, { time: true })}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <Pagination
                count={balance.history.count}
                page={page}
                onChange={handleChangePage}
              />
            </>
          )}
        </Box>
      </Paper>
    </Root>
  );
};

export default HistoryPage;
